import { useCallback } from 'react';
import { useStoreApi } from '@xyflow/react';
import { buildNavigationEdge } from '../utils/flowEdgeUtils';

// Left-to-right workflow layout: connect when nodes are on the same row and
// horizontally close (not when stacked vertically in the same column).
const MAX_HORIZONTAL_GAP = 200;
const MAX_VERTICAL_ALIGNMENT = 90;
const DEFAULT_NODE_WIDTH = 240;
const DEFAULT_NODE_HEIGHT = 80;

function getNodeBounds(internalNode) {
  const position = internalNode.internals.positionAbsolute;
  const width = internalNode.measured?.width ?? DEFAULT_NODE_WIDTH;
  const height = internalNode.measured?.height ?? DEFAULT_NODE_HEIGHT;

  return {
    left: position.x,
    right: position.x + width,
    top: position.y,
    bottom: position.y + height,
    centerY: position.y + height / 2,
    id: internalNode.internals.userNode.id,
  };
}

function getClosestEdgeCandidate(draggedInternal, nodeLookup) {
  if (!draggedInternal) return null;

  const dragged = getNodeBounds(draggedInternal);
  let best = null;
  let bestGap = Infinity;

  nodeLookup.forEach((internalNode, id) => {
    if (id === dragged.id) return;

    const other = getNodeBounds(internalNode);
    const verticalOffset = Math.abs(other.centerY - dragged.centerY);
    if (verticalOffset > MAX_VERTICAL_ALIGNMENT) return;

    let sourceId;
    let targetId;
    let horizontalGap;

    if (other.left < dragged.left) {
      // Other node is to the left: connect other -> dragged
      sourceId = other.id;
      targetId = dragged.id;
      horizontalGap = dragged.left - other.right;
    } else {
      // Other node is to the right: connect dragged -> other
      sourceId = dragged.id;
      targetId = other.id;
      horizontalGap = other.left - dragged.right;
    }

    if (horizontalGap < 0) horizontalGap = 0;
    if (horizontalGap > MAX_HORIZONTAL_GAP) return;

    if (horizontalGap < bestGap) {
      bestGap = horizontalGap;
      best = { source: sourceId, target: targetId };
    }
  });

  return best;
}

function buildProximityEdge(candidate, actionIndex) {
  if (!candidate) return null;

  const { source, target } = candidate;
  if (source === target) return null;

  return {
    ...buildNavigationEdge({ source, target, actionIndex, label: '' }),
    className: 'temp',
    style: { strokeDasharray: '5,5', stroke: '#6c757d' },
  };
}

export default function useProximityConnect({ setEdges, onProximityConnect, workflow }) {
  const store = useStoreApi();

  const onNodeDrag = useCallback(
    (_, node) => {
      const { nodeLookup } = store.getState();
      const draggedInternal = nodeLookup.get(node.id);
      const candidate = getClosestEdgeCandidate(draggedInternal, nodeLookup);

      const sourceStep = workflow?.workflow_steps?.find((step) => step.id === candidate?.source);
      const actionIndex = (sourceStep?.actions || []).length;
      const proximityEdge = buildProximityEdge(candidate, actionIndex);

      setEdges((currentEdges) => {
        const withoutTemp = currentEdges.filter((edge) => edge.className !== 'temp');
        if (!proximityEdge) return withoutTemp;
        return [...withoutTemp, proximityEdge];
      });
    },
    [setEdges, store, workflow]
  );

  const onNodeDragStop = useCallback(
    (_, node) => {
      setEdges((currentEdges) => {
        const tempEdge = currentEdges.find(
          (edge) =>
            edge.className === 'temp' && (edge.source === node.id || edge.target === node.id)
        );
        const withoutTemp = currentEdges.filter((edge) => edge.className !== 'temp');

        if (tempEdge) {
          const { className, style, ...permanentEdge } = tempEdge;
          onProximityConnect?.(permanentEdge);
        }

        return withoutTemp;
      });
    },
    [setEdges, onProximityConnect]
  );

  return { onNodeDrag, onNodeDragStop };
}
